import React, { useState, Fragment, ChangeEvent } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../reducers";
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  DialogActions,
  Button
} from "@material-ui/core";
import {
  toggleAddingDialog,
  resetAddingState,
  updateSearchKeyword
} from "../../actions";
import { AddingDataType, TorrentStatus, ToastVariant } from "../../types";
import {
  thunkSubmitAdding,
  thunkPushDownload,
  thunkDeleteTorrents,
  thunkFetchTorrents
} from "../../thunks";
import { Add as AddIcon } from "@material-ui/icons";
import Toast from "../Shared/Toast";
import LoadingButton from "../Shared/LoadingButton";
import FileButton from '../Shared/FileButton';
import FolderField from '../Shared/FolderField';


const useStyles = makeStyles(theme => ({
  dataType: {
    flexDirection: 'initial'
  },
}));

export default function Add() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [folder, setFolder] = useState('');

  const currentFolder = useSelector((state: RootState) => state.setting.folder);
  const addingPending = useSelector((state: RootState) => state.adding.pending);

  const showAddingDialog = () => {
    setFolder(currentFolder);
    dispatch(toggleAddingDialog(true));
  }

  const open = useSelector((state: RootState) => state.adding.show);
  const handleClose = () => dispatch(toggleAddingDialog(false));
  const [dataType, setDataType] = useState(AddingDataType.FILE);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [filePath, setFilePath] = useState('');
  const [filePathError, setFilePathError] = useState('');
  const [fileB64, setFileB64] = useState('');



  const handleUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setUrl(value);
    setUrlError('');
    try {
      new URL(value);
    } catch {
      setUrlError('Invalid URL');
    }
  }

  const handleFilePick = (path: string, base64?: string) => {
    setFilePath(path);
    setFilePathError('');
    setFileB64('');
    if (!base64) {
      setFilePathError('Invalid torrent file');
    } else {
      setFileB64(base64);
    }
  }

  const handleFolderChange = (e: ChangeEvent<HTMLInputElement>) =>  {
    setFolder(e.target.value);
  }

  const submitable = !!folder && (
    dataType === AddingDataType.FILE
      ? (!filePathError && fileB64)
      : (!urlError && url)
  );

  const handleSubmit = () => {
    dispatch(thunkSubmitAdding({
      type: dataType === AddingDataType.FILE ? 'BASE64' : 'URL',
      data: dataType === AddingDataType.FILE ? fileB64 : url,
      folder,
    }));
  }

  const added = useSelector((state: RootState) => state.adding.added);
  const addingError = useSelector((state: RootState) => state.adding.error);
  let snackbarMessage = '';
  let snackbarVariant: ToastVariant;
  let actions: React.ReactNode[] = [];
  if (added && added.id) {
    const id = added.id;
    if (added.status === TorrentStatus.CONFLICTED) {
      snackbarMessage = `${added.title} is conflicted`;
      snackbarVariant = 'warning';
      const showConfliction = () => {
        dispatch(updateSearchKeyword(added.title));
        dispatch(thunkFetchTorrents('refresh'));
        handleCloseSnackbar();
      }
      const forceDownload = () => {
        dispatch(thunkPushDownload(id));
        handleCloseSnackbar();
      }
      const deleteTorrent = () => {
        dispatch(thunkDeleteTorrents([id], true));
        handleCloseSnackbar();
      }
      actions = [
        <Button key="show-confliction" size="small" color="secondary" onClick={showConfliction}>Show Confliction</Button>,
        <Button key="force-download" size="small" color="secondary" onClick={forceDownload}>Force Download</Button>,
        <Button key="delete" size="small" color="secondary" onClick={deleteTorrent}>Delete</Button>,
      ]
    } else if (added.status === TorrentStatus.ERROR) {
      snackbarMessage = `Torrent error: ${added.message}`;
      snackbarVariant = 'warning';
    } else {
      snackbarMessage = `${added.title} was added successfully`;
      snackbarVariant = 'success';
    }
  } else if (addingError) {
    snackbarMessage = `${addingError.message}`;
    snackbarVariant = 'error';
  }
  const handleCloseSnackbar = () => {
    dispatch(resetAddingState());
  }


  return (
    <Fragment>
      <LoadingButton pending={addingPending} color="inherit" onClick={showAddingDialog}>
        <AddIcon />
      </LoadingButton>
      <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
        <DialogTitle>
          Add Torrent
      </DialogTitle>
        <DialogContent dividers style={{ width: '600px' }}>
          <Grid container spacing={3}>
            <Grid item xs={2} className="form-row-label">
              Type:
          </Grid>
            <Grid item xs={10} className="form-row-label">
              <RadioGroup aria-label="gender" className={classes.dataType} value={dataType}
                onChange={({ target }) => setDataType(target.value as AddingDataType)}>
                {Object.values(AddingDataType).map((dataType: AddingDataType) =>
                  <FormControlLabel key={dataType} value={dataType} control={<Radio />} label={dataType} />
                )}
              </RadioGroup>
            </Grid>
            {dataType === AddingDataType.URL &&
              <Fragment>
                <Grid item xs={3} className="form-row-label">
                  URL:
                </Grid>
                <Grid item xs={9}>
                  <TextField margin="dense" variant="outlined" value={url} fullWidth
                    error={!!urlError}
                    label={urlError}
                    onChange={handleUrlChange} />
                </Grid>
              </Fragment>
            }
            {dataType === AddingDataType.FILE &&
              <Fragment>
                <Grid item xs={3} className="form-row-label">
                  Torrent:
                </Grid>
                <Grid item xs={7} className="form-row-label">
                  <TextField
                    error={!!filePathError}
                    label={filePathError}
                    margin="dense" variant="outlined" value={filePath} fullWidth />
                </Grid>
                <Grid item xs={2} className="form-row-label">
                  <FileButton onPick={handleFilePick}>Pick</FileButton>
                </Grid>
              </Fragment>
            }
            <Fragment>
              <Grid item xs={3} className="form-row-label">
                Download folder:
              </Grid>
              <Grid item xs={9}>
                <FolderField value={folder} onChange={handleFolderChange} />
              </Grid>
            </Fragment>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleSubmit} disabled={!submitable} color="primary">
            Ok
        </Button>
          <Button autoFocus onClick={handleClose} color="primary">
            Cancel
        </Button>
        </DialogActions>
      </Dialog >
      <Toast message={snackbarMessage} variant={snackbarVariant} onClose={handleCloseSnackbar} actions={actions} />
    </Fragment>
  )
}